import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Check, CheckCheck, Paperclip } from "lucide-react";
import type { FC } from "react";
import { ChatListRowAvatar } from "web_myplann/Chat/Components/ChatListRowAvatar";
import { formatChatListTimestamp } from "web_myplann/Chat/Helpers/chatFormatting";
import type { ChatConversation } from "web_myplann/Chat/Types/chatConversation";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	conversation: ChatConversation;
	isLast: boolean;
	onClick: () => void;
};

export const ChatListRow: FC<Props> = ({ conversation, isLast, onClick }) => {
	const theme = useTheme();
	const { t } = useResource();

	const { title, last_message } = conversation;
	const unread = last_message ? !last_message.is_read : false;
	const preview = last_message
		? last_message.is_mine
			? `${t(Resources.Chat.youPrefix)} ${last_message.content}`
			: last_message.content
		: "";

	return (
		<Stack
			direction="row"
			alignItems="center"
			gap={1.2}
			px={1.6}
			py={1.2}
			onClick={onClick}
			width="100%"
			textAlign="left"
			bgcolor="transparent"
			borderBottom={isLast ? 0 : 1}
			borderColor="surface.border"
			style={{ cursor: "pointer" }}
		>
			<ChatListRowAvatar name={title} />

			<Stack flex={1} minWidth={0}>
				<Stack direction="row" alignItems="baseline" gap={0.8}>
					<Typography
						variant={unread ? "bodyStrong" : "bodyMedium"}
						noWrap
						flex={1}
						minWidth={0}
					>
						{title}
					</Typography>
					{last_message?.created_at && (
						<Typography
							variant="hint"
							flexShrink={0}
							color={
								unread ? theme.palette.blue[500] : theme.palette.text.disabled
							}
						>
							{formatChatListTimestamp(last_message.created_at)}
						</Typography>
					)}
				</Stack>

				<Stack direction="row" alignItems="center" gap={0.6} mt={0.1}>
					{last_message?.is_mine &&
						(last_message.is_read ? (
							<CheckCheck
								size={13}
								color={theme.palette.blue[400]}
								style={{ flexShrink: 0 }}
							/>
						) : (
							<Check
								size={13}
								color={theme.palette.grey[400]}
								style={{ flexShrink: 0 }}
							/>
						))}
					{last_message?.is_attachment && (
						<Paperclip
							size={12}
							color={theme.palette.grey[400]}
							style={{ flexShrink: 0 }}
						/>
					)}
					<Typography
						variant="caption"
						noWrap
						flex={1}
						minWidth={0}
						fontWeight={unread ? 500 : 400}
						color={
							unread ? theme.palette.text.primary : theme.palette.text.secondary
						}
					>
						{preview}
					</Typography>
					{unread && (
						<Box
							width={8}
							height={8}
							borderRadius={theme.shape.pillRadius}
							bgcolor={theme.palette.blue[500]}
							flexShrink={0}
						/>
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};
